import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export interface NewAgentStepTimelineProps {
  steps: readonly string[];
  currentIndex: number;
  /** Furthest step the user has reached (inclusive); steps up to this index are clickable. */
  maxReachableIndex?: number;
  onStepClick?: (index: number) => void;
  disabled?: boolean;
}

export const NewAgentStepTimeline: React.FC<NewAgentStepTimelineProps> = ({
  steps,
  currentIndex,
  maxReachableIndex = currentIndex,
  onStepClick,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const getStepLabel = (step: string) => {
    const label = t(
      `agent.new.guided.steps.${step}.translation`,
      step.replace(/_/g, ' ')
    );
    return label.replace(/^\d+\.?\s*/, '').trim();
  };

  return (
    <nav
      className="px-4 sm:px-6"
      aria-label={t(
        'agent.new.guided.steps.navigation.translation',
        'Setup progress'
      )}
    >
      <ol className="flex w-full min-w-0 items-start">
        {steps.map((step, index) => {
          const isActive = index === currentIndex;
          const isComplete = index < currentIndex;
          const isReachableAhead =
            index > currentIndex && index <= maxReachableIndex;
          const hasBeenReached = index <= maxReachableIndex;
          const isFirst = index === 0;
          const isLast = index === steps.length - 1;
          const canNavigateToStep =
            !disabled &&
            index !== currentIndex &&
            hasBeenReached &&
            onStepClick != null;

          const circleClassName = cn(
            'mx-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors sm:mx-1 sm:h-9 sm:w-9',
            isActive && 'border-brand bg-brand text-brand-foreground',
            !isActive &&
              (isComplete || isReachableAhead) &&
              'border-brand bg-transparent text-brand',
            !isActive &&
              !isComplete &&
              !isReachableAhead &&
              'border-border bg-transparent text-muted-foreground',
            canNavigateToStep &&
              'cursor-pointer hover:border-brand-hover hover:bg-brand/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
          );

          return (
            <li
              key={step}
              className="flex min-w-0 flex-1 flex-col items-center"
              aria-current={isActive ? 'step' : undefined}
            >
              <div className="flex w-full items-center">
                <div
                  className={cn(
                    'h-0.5 min-w-0 flex-1 rounded-full',
                    isFirst
                      ? 'invisible'
                      : hasBeenReached
                        ? 'bg-brand'
                        : 'bg-border'
                  )}
                  aria-hidden
                />
                {canNavigateToStep ? (
                  <button
                    type="button"
                    onClick={() => onStepClick(index)}
                    className={circleClassName}
                    aria-label={t(
                      'agent.new.guided.steps.go_to.translation',
                      'Go to step {{number}}: {{label}}',
                      { number: index + 1, label: getStepLabel(step) }
                    )}
                  >
                    {index + 1}
                  </button>
                ) : (
                  <div className={circleClassName}>{index + 1}</div>
                )}
                <div
                  className={cn(
                    'h-0.5 min-w-0 flex-1 rounded-full',
                    isLast
                      ? 'invisible'
                      : index < maxReachableIndex
                        ? 'bg-brand'
                        : 'bg-border'
                  )}
                  aria-hidden
                />
              </div>
              {isActive && (
                <p className="mt-2 w-full max-w-[6.5rem] px-0.5 text-center text-xs font-medium leading-tight text-foreground sm:max-w-[7.5rem] sm:text-sm">
                  {getStepLabel(step)}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
